import {useEffect, useState} from 'react';

import {
  DAY_NAMES_FULL,
  DAY_NAMES_SHORT,
  MONTH_NAMES_FULL,
  MONTH_NAMES_SHORT,
  parseISOWithoutConversion,
} from '../../../utils';

export const useDateRange = ({
  recordingsData,
  dateRange,
  selectedSuborgs,
  isMultiOrg,
}) => {
  // Process recordings into daily data for charts
  // filterFn: optional predicate to filter which recordings to count
  const processMonthlyData = (
    recordings,
    startDate,
    endDate,
    filterFn = null,
  ) => {
    const dailyData = {};
    const currentDate = new Date(startDate);

    // Ensure endDate doesn't exceed today
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    const maxDate = endDate > today ? today : endDate;

    while (currentDate <= maxDate) {
      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      const day = String(currentDate.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      dailyData[dateStr] = {
        date: dateStr,
        value: 0,
      };
      currentDate.setDate(currentDate.getDate() + 1);
    }

    recordings.forEach((recording) => {
      // Skip if filterFn provided and recording doesn't match
      if (filterFn && !filterFn(recording)) {
        return;
      }

      try {
        const parsed = parseISOWithoutConversion(
          recording.checkin_datetime_in_org_zone,
        );
        if (!parsed) {
          return;
        }

        const year = parsed.year;
        const month = String(parsed.month).padStart(2, '0');
        const day = String(parsed.day).padStart(2, '0');
        const dateStr = `${year}-${month}-${day}`;
        if (dailyData[dateStr]) {
          dailyData[dateStr].value += 1;
        }
      } catch (err) {
        console.error('Error processing recording date:', err);
      }
    });

    const dataArray = Object.values(dailyData).sort(
      (a, b) => new Date(a.date) - new Date(b.date),
    );

    const monthGroups = {};
    dataArray.forEach((item, index) => {
      // Parse date as local date, not UTC
      const [year, month, day] = item.date.split('-').map(Number);
      const itemDate = new Date(year, month - 1, day);
      const monthKey = `${itemDate.getFullYear()}-${itemDate.getMonth()}`;

      if (!monthGroups[monthKey]) {
        monthGroups[monthKey] = [];
      }
      monthGroups[monthKey].push(index);
    });

    let previousMonth = null;
    dataArray.forEach((item, index) => {
      // Parse date as local date, not UTC
      const [year, month, day] = item.date.split('-').map(Number);
      const itemDate = new Date(year, month - 1, day);
      const currentMonth = itemDate.getMonth();
      const currentYear = itemDate.getFullYear();
      const monthKey = `${currentYear}-${currentMonth}`;

      item.isMonthBoundary =
        currentMonth !== previousMonth ||
        (index < dataArray.length - 1 &&
          new Date(dataArray[index + 1].date).getMonth() !== currentMonth);

      const monthIndices = monthGroups[monthKey];
      const centerIndex = monthIndices[Math.floor(monthIndices.length / 2)];

      if (index === centerIndex) {
        item.monthLabel = `${MONTH_NAMES_SHORT[currentMonth]} '${(currentYear % 100).toString().padStart(2, '0')}`;
      } else {
        item.monthLabel = '';
      }

      previousMonth = currentMonth;

      item.fullDate = `${MONTH_NAMES_FULL[currentMonth]} ${itemDate.getDate()}, ${currentYear}`;
    });

    return dataArray;
  };

  // Filter predicate for flagged/impaired recordings
  const isFlaggedRecording = (recording) =>
    recording.result === '1' || recording.result === 1;

  const [metrics, setMetrics] = useState({
    totalFlags: 0,
    flagsPerWeek: 0,
  });

  const [filteredData, setFilteredData] = useState({
    complianceData: [],
    flaggedData: [],
    frequentlyFlaggedData: [],
    flagByDayData: [],
  });

  useEffect(() => {
    // For "All Time" mode with no data, skip processing (can't calculate date range)
    // For filtered mode, process even with no data to show empty charts
    if (recordingsData.length === 0 && dateRange.isAllTime) return;

    let startDate, endDate;

    if (dateRange.isAllTime) {
      const dates = recordingsData.map((record) => {
        const parsed = parseISOWithoutConversion(
          record.checkin_datetime_in_org_zone,
        );
        return parsed ? parsed.toDate().getTime() : 0;
      });
      startDate = new Date(Math.min(...dates));
      startDate.setDate(1);
      startDate.setHours(0, 0, 0, 0);

      endDate = new Date();
      endDate.setDate(
        new Date(endDate.getFullYear(), endDate.getMonth() + 1, 0).getDate(),
      );
      endDate.setHours(23, 59, 59, 999);
    } else {
      if (dateRange.start) {
        const [startYear, startMonth, startDay] = dateRange.start
          .split('-')
          .map((num) => parseInt(num, 10));
        startDate = new Date(startYear, startMonth - 1, startDay, 0, 0, 0, 0);
      } else {
        startDate = new Date(2024, 4, 1, 0, 0, 0, 0);
      }

      if (dateRange.end) {
        const [endYear, endMonth, endDay] = dateRange.end
          .split('-')
          .map((num) => parseInt(num, 10));
        endDate = new Date(endYear, endMonth - 1, endDay, 23, 59, 59, 999);
      } else {
        const now = new Date();
        const lastDay = new Date(
          now.getFullYear(),
          now.getMonth() + 1,
          0,
        ).getDate();
        endDate = new Date(
          now.getFullYear(),
          now.getMonth(),
          lastDay,
          23,
          59,
          59,
          999,
        );
      }
    }

    // Date filtering is now handled by the API, only apply suborg filter for admin users
    const filteredRecordings = isMultiOrg
      ? recordingsData.filter((recording) => {
          return (
            selectedSuborgs.length === 0 ||
            selectedSuborgs.includes(recording.company_name)
          );
        })
      : recordingsData;

    const complianceByMonth = processMonthlyData(
      filteredRecordings,
      startDate,
      endDate,
    );

    const processFlagsByDayOfWeek = (recordings) => {
      const dayData = DAY_NAMES_SHORT.map((day, index) => ({
        day,
        fullDay: DAY_NAMES_FULL[index],
        value: 0,
      }));

      recordings.forEach((recording) => {
        if (recording.result === '1' || recording.result === 1) {
          const parsed = parseISOWithoutConversion(
            recording.checkin_datetime_in_org_zone,
          );
          if (!parsed) return;
          const dayIndex = parsed.toDate().getDay();
          dayData[dayIndex].value += 1;
        }
      });

      return dayData;
    };

    const processFrequentlyFlaggedData = (recordings) => {
      const employeeCounts = {};

      recordings.forEach((recording) => {
        if (recording.result === '1' || recording.result === 1) {
          const firstName = recording.first_name || 'Unknown';
          const lastName = recording.last_name || '';
          const employeeName = firstName;

          // For admin users, show location under the name
          if (isMultiOrg) {
            const companyName = recording.company_name || 'Unknown Location';
            const displayName = `${firstName} ${lastName} (${companyName})`.trim();
            employeeCounts[displayName] =
              (employeeCounts[displayName] || 0) + 1;
          } else {
            // For non-admin users, just show employee name
            employeeCounts[employeeName] =
              (employeeCounts[employeeName] || 0) + 1;
          }
        }
      });

      // If no data, return empty array but chart will still show axes
      return Object.entries(employeeCounts)
        .map(([name, value]) => ({name, value}))
        .sort((a, b) => b.value - a.value)
        .slice(0, isMultiOrg ? 8 : 4);
    };

    const flaggedByMonth = processMonthlyData(
      filteredRecordings,
      startDate,
      endDate,
      isFlaggedRecording,
    );
    const frequentlyFlagged = processFrequentlyFlaggedData(filteredRecordings);
    const flagsByDay = processFlagsByDayOfWeek(filteredRecordings);

    const flaggedRecordings = filteredRecordings.filter(
      (rec) => rec.result === '1' || rec.result === 1,
    );

    const totalFlags = flaggedRecordings.length;

    const totalWeeks = Math.max(
      1,
      Math.ceil((endDate - startDate) / (7 * 24 * 60 * 60 * 1000)),
    );
    const flagsPerWeek =
      totalWeeks > 0 ? Math.round((totalFlags / totalWeeks) * 10) / 10 : 0;

    setMetrics({
      totalFlags,
      flagsPerWeek,
    });

    setFilteredData({
      complianceData: complianceByMonth,
      flaggedData: flaggedByMonth,
      frequentlyFlaggedData: frequentlyFlagged,
      flagByDayData: flagsByDay,
    });
  }, [recordingsData, dateRange, selectedSuborgs, isMultiOrg]);

  return {metrics, filteredData};
};
